import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { api } from '../utils/api';
import { Check, Copy } from './Icons';

const TABS = [
  { id: 'summary', label: 'Summary' },
  { id: 'fields',  label: 'Fields' },
  { id: 'receive', label: 'What you receive' },
  { id: 'raw',     label: 'Raw request' },
];

const FIELD_LABELS = {
  given_name: 'Given name', family_name: 'Family name', birth_date: 'Date of birth',
  birth_place: 'Place of birth', nationality: 'Nationality', issuing_country: 'Issuing country',
  document_number: 'Document number', expiry_date: 'Document expiry', resident_address: 'Address',
  age_over_18: 'Age over 18', age_over_21: 'Age over 21',
};

// Where each requested element ends up in the verify-token response.
const RECEIVE = {
  openid4vp: [
    ['given_name', 'identity.givenName'], ['family_name', 'identity.familyName'],
    ['birth_date', 'identity.birthDate'], ['birth_place', 'identity.birthPlace'],
    ['nationality', 'identity.nationality'], ['issuing_country', 'identity.issuingCountry'],
    ['resident_address', 'identity.address, city, postalCode, country'],
    ['document_number', 'identity.documentNumber'], ['expiry_date', 'identity.documentExpiry'],
    ['age_over_18', 'identity.ageOver18'], ['age_over_21', 'identity.ageOver21'],
  ],
  dc_api: [
    ['given_name', 'identity.givenName'], ['family_name', 'identity.familyName'],
    ['birth_date', 'identity.birthDate'],
    ['nationality', 'identity.nationality', 'Falls back to the issuing country'],
    ['issuing_country', 'wallet.issuingCountry'],
    ['document_number', 'identity.documentNumber'], ['expiry_date', 'identity.documentExpiry'],
    ['age_over_18', 'identity.ageOver18', 'Calculated from date of birth if the pass has no value'],
    ['age_over_21', 'identity.ageOver21', 'Calculated from date of birth if the pass has no value'],
  ],
};

export default function RequestPreviewDrawer({ wallet, pidAttributes, onClose }) {
  const [tab, setTab]         = useState('summary');
  const [preview, setPreview] = useState(null);
  const [error, setError]     = useState(null);
  const attrsKey = (pidAttributes || []).join(',');

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.previewWalletRequest(wallet.id, { pid_attributes: attrsKey ? attrsKey.split(',') : undefined })
      .then(p => { if (!cancelled) setPreview(p); })
      .catch(e => { if (!cancelled) setError(e.message || 'Could not load the request.'); });
    return () => { cancelled = true; };
  }, [wallet.id, attrsKey]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const credentials = preview?.payload?.dcql_query?.credentials || [];

  return createPortal(
    <div className="vt-drawerWrap" role="dialog" aria-modal="true" aria-label={`${wallet.name} request`}>
      <div className="vt-drawerBackdrop" onClick={onClose} />
      <aside className="vt-drawer">
        <div className="vt-drawerHead">
          <div>
            <div className="vt-eyebrow" style={{ marginBottom: 6 }}>Request preview</div>
            <h2 className="vt-panelTitle">{wallet.name}</h2>
          </div>
          <button type="button" className="vt-iconBtn" onClick={onClose} aria-label="Close">Close</button>
        </div>

        <div className="vt-tabs" role="tablist">
          {TABS.map(t => (
            <button key={t.id} type="button" role="tab" aria-selected={tab === t.id}
              className={['vt-tab', tab === t.id ? 'vt-tab--on' : ''].filter(Boolean).join(' ')}
              onClick={() => setTab(t.id)}>
              {t.label}
            </button>
          ))}
        </div>

        <div className="vt-drawerBody">
          {error && <div className="vt-alert vt-alert--error">{error}</div>}
          {!preview && !error && <p className="vt-muted">Building request…</p>}
          {preview && tab === 'summary' && <Summary preview={preview} credentials={credentials} />}
          {preview && tab === 'fields' && <Fields credentials={credentials} />}
          {preview && tab === 'receive' && <Receive preview={preview} credentials={credentials} />}
          {preview && tab === 'raw' && <Raw preview={preview} />}
        </div>
      </aside>
    </div>,
    document.body
  );
}

function Summary({ preview, credentials }) {
  const p = preview.payload || {};
  const doctypes = credentials.map(c => c.meta?.doctype_value).filter(Boolean);
  const rows = preview.protocol === 'openid4vp'
    ? [
        ['Protocol', 'OpenID4VP via the Veritage EUDI verifier'],
        ['Reaches the wallet by', 'QR code or deep link (mdoc-openid4vp://)'],
        ['Signed by', 'The EUDI verifier, with your relying party access certificate'],
        ['Credential', doctypes.join(', ')],
        ['Response', 'Returned to the verifier, then checked by Veritage'],
      ]
    : [
        ['Protocol', `OpenID4VP 1.0 over the Digital Credentials API (${preview.protocol_name || preview.protocol})`],
        ['Reaches the wallet by', 'navigator.credentials.get() in the browser'],
        ['Signed by', p.client_id ? `${p.client_id}` : '—'],
        ['Accepted credentials', credentials.length > 1 ? `Any one of: ${doctypes.join(', ')}` : doctypes.join(', ')],
        ['Response', p.response_mode === 'dc_api.jwt' ? 'Encrypted to a per-request key (ECDH-ES)' : (p.response_mode || '—')],
        ['Allowed origins', (p.expected_origins || []).join(', ') || '—'],
      ];
  return (
    <dl className="vt-defList">
      {rows.map(([k, v]) => (
        <div className="vt-defRow" key={k}><dt>{k}</dt><dd>{v}</dd></div>
      ))}
    </dl>
  );
}

const namespaces = (cred) => [...new Set((cred.claims || []).map(c => c.path[0]))];

function Fields({ credentials }) {
  return credentials.map(cred => {
    const sets = cred.claim_sets;
    const required = (id) => !sets || sets.every(set => set.includes(id));
    return (
      <div key={cred.id} style={{ marginBottom: 22 }}>
        <div className="vt-sectionLabel" style={{ textTransform: 'none', letterSpacing: 0 }}>
          <code>{cred.meta?.doctype_value}</code>
          {namespaces(cred).length === 1 && <> · namespace <code>{namespaces(cred)[0]}</code></>}
        </div>
        <table className="vt-table vt-table--compact">
          <thead><tr><th className="vt-colLabel">Field</th><th>Element</th><th className="vt-colShort">Required</th></tr></thead>
          <tbody>
            {(cred.claims || []).map(c => {
              const el = c.path[c.path.length - 1];
              return (
                <tr key={c.path.join('/')}>
                  <td>{FIELD_LABELS[el] || el}</td>
                  <td><code>{namespaces(cred).length === 1 ? el : c.path.join(' / ')}</code></td>
                  <td>{required(c.id || el) ? 'Yes' : 'Optional'}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {(cred.claims || []).every(c => c.intent_to_retain === false) && (
          <p className="vt-panelFoot" style={{ marginTop: 10 }}>Every field is requested with <code>intent_to_retain: false</code>.</p>
        )}
      </div>
    );
  });
}

function Receive({ preview, credentials }) {
  const requested = new Set(credentials.flatMap(c => (c.claims || []).map(cl => cl.path[cl.path.length - 1])));
  const rows = (RECEIVE[preview.protocol] || []).filter(([el]) => requested.has(el));
  return (
    <>
      <p className="vt-panelSub" style={{ marginTop: 0, marginBottom: 16 }}>
        Fields in the <code>POST /api/v1/sessions/verify-token</code> response. The raw wallet values are also returned in <code>rawClaims</code>.
      </p>
      <table className="vt-table vt-table--compact">
        <thead><tr><th className="vt-colLabel">Wallet field</th><th>You receive</th></tr></thead>
        <tbody>
          {rows.map(([el, target, note]) => (
            <tr key={el}>
              <td>{FIELD_LABELS[el] || el}</td>
              <td><code>{target}</code>{note && <div className="vt-rowMeta">{note}</div>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function Raw({ preview }) {
  const [copied, setCopied] = useState(null);
  const blocks = preview.header
    ? [['JWS header', preview.header], ['JWS payload', preview.payload]]
    : [['Request sent to the EUDI verifier', preview.payload]];
  const copy = (label, value) => {
    navigator.clipboard.writeText(JSON.stringify(value, null, 2));
    setCopied(label);
    setTimeout(() => setCopied(null), 1500);
  };
  return blocks.map(([label, value]) => (
    <div className="vt-keyBox" key={label} style={{ marginBottom: 14 }}>
      <div className="vt-keyBoxHead">
        <span className="vt-keyBoxLabel">{label}</span>
        <button type="button" className="vt-keyBoxBtn" onClick={() => copy(label, value)}>
          {copied === label ? <Check size={13} /> : <Copy size={13} />} {copied === label ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="vt-keyBoxValue vt-json">{JSON.stringify(value, null, 2)}</pre>
    </div>
  ));
}
